import { fromJS, List, Map } from "immutable";
import moment from "moment";
import Task from "~/records/record.task.jsx";
import TaskMap from "~/records/record.task_map.jsx";
import * as actionTypes from "~/store/action_types";
import { dateStringToUnixTimestamp } from "~/utils/date.jsx";

export const task = (state = new Task(), action) => {
  switch (action.type) {
    case actionTypes.ADD_COMMENT_LOADING:
      return new Task({
        ...state.toObject(),
        comments_count: state.comments_count + 1,
      });
    case actionTypes.MOVE_COMMENT_TO_TRASH_SUCCESS:
      return new Task({
        ...state.toObject(),
        comments_count: state.comments_count - 1,
      });
    case actionTypes.FETCH_COMMENTS_LOADING:
      return new Task({
        ...state.toObject(),
        _fetchingComments: true,
      });
    case actionTypes.FETCH_COMMENTS_SUCCESS:
    case actionTypes.FETCH_COMMENTS_ERROR:
      return new Task({
        ...state.toObject(),
        _fetchingComments: false,
      });
    case actionTypes.EDIT_TASK_LOADING:
    case actionTypes.BATCH_EDIT_TASKS_LOADING: {
      const { attributes = {}, position } = action;
      if (attributes?.due_on && attributes?.start_on) {
        return new Task({
          ...state.toObject(),
          ...{
            ...attributes,
            due_on: moment(attributes?.due_on).unix(),
            start_on: moment(attributes?.start_on).unix(),
          },
          labels: attributes.labels ? new List(attributes.labels) : state.labels,
          position: typeof position !== "undefined" ? position : state.position,
          _pending: true,
        });
      }
      return new Task({
        ...state.toObject(),
        ...attributes,
        labels: attributes.labels ? new List(attributes.labels) : state.labels,
        position: typeof position !== "undefined" ? position : state.position,
        _pending: true,
      });
    }
    case actionTypes.MOVE_TASK_TO_TRASH_LOADING:
      return new Task({
        ...state.toObject(),
        _pending: true,
      });
    case actionTypes.OPEN_TASK_LOADING:
    case actionTypes.COMPLETE_TASK_ERROR: {
      return new Task({
        ...state.toObject(),
        is_completed: false,
      });
    }
    case actionTypes.COMPLETE_TASK_LOADING: {
      return new Task({
        ...state.toObject(),
        is_completed: true,
        completed_on: moment().utc().unix(),
      });
    }
    case actionTypes.OPEN_TASK_ERROR: {
      return new Task({
        ...state.toObject(),
        is_completed: true,
      });
    }
    case actionTypes.EDIT_TASK_ERROR: {
      const { oldTaskData } = action;
      if (oldTaskData) {
        return new Task({
          ...state.toObject(),
          ...action.oldTaskData,
        });
      }
    }
    // eslint-disable-next-line no-fallthrough
    case actionTypes.MOVE_TASK_TO_TRASH_ERROR:
    case actionTypes.BATCH_EDIT_TASKS_ERROR:
      return new Task({
        ...state.toObject(),
        _pending: false,
      });
    case actionTypes.REOPEN_TASK_LIST_SUCCESS:
    case actionTypes.REOPEN_TASK_LIST_LOADING: {
      const { timestamp: completed_on } = action;

      if (!state.get(`is_completed`) || state.get(`completed_on`) < completed_on) {
        return state;
      }

      return new Task({
        ...state.toObject(),
        is_completed: false,
        completed_on: null,
      });
    }
    case actionTypes.COMPLETE_TASK_LIST_LOADING: {
      if (state.get(`is_completed`)) {
        return state;
      }

      const { timestamp: completed_on } = action;

      return new Task({
        ...state.toObject(),
        is_completed: true,
        completed_on,
      });
    }
    case actionTypes.COMPLETE_TASK_LIST_SUCCESS: {
      if (state.get(`is_completed`)) {
        return state;
      }

      const { response = {}, taskListId } = action;
      const { entities = {} } = response;
      const { task_lists = {} } = entities;
      const { completed_on } = task_lists[`${taskListId}`] || {};

      return new Task({
        ...state.toObject(),
        is_completed: true,
        completed_on,
      });
    }
    case actionTypes.MOVE_TASK_LIST_TO_TRASH_SUCCESS:
      return new Task({
        ...state.toObject(),
        is_trashed: true,
      });
    case actionTypes.RESTORE_TASK_FROM_TRASH_SUCCESS:
      return new Task({
        ...state.toObject(),
        is_trashed: false,
        _pending: false,
      });
    case actionTypes.MOVE_TASK_TO_TRASH_SUCCESS: {
      const { response = {}, id } = action;
      const { entities = {} } = response;
      const { tasks = {} } = entities;
      const { trashed_by_id = 0, trashed_on = null } = tasks[id];
      return new Task({
        ...state.toObject(),
        is_trashed: true,
        trashed_by_id,
        trashed_on,
        _pending: false,
      });
    }
    case actionTypes.TASK_MOVE_TO_TRASH_FROM_EVENT_STREAM: {
      const { response = {} } = action;
      const { entities = {}, result: id } = response;
      const { tasks = {} } = entities;

      return new Task(
        fromJS({
          ...state.toObject(),
          ...tasks[id],
          is_trashed: true,
        })
      );
    }
    case actionTypes.TASK_RESTORED_FROM_TRASH_FROM_EVENT_STREAM: {
      const { response = {} } = action;
      const { entities = {}, result: id } = response;
      const { tasks = {} } = entities;

      return new Task(
        fromJS({
          ...state.toObject(),
          ...tasks[id],
          is_trashed: false,
        })
      );
    }
    case actionTypes.REORDER_TASKS_ERROR:
      return new Task({
        ...state.toObject(),
        _pending: false,
      });
    case actionTypes.ANGULAR_INITIALIZE_TASKS:
      return new Task({
        ...state.toObject(),
        is_trashed: action.response.result.trashed_task_ids.indexOf(state.id) > -1,
        is_completed: action.response.result.completed_task_ids.indexOf(state.id) > -1,
      });
    case actionTypes.ADD_TASK_LOADING: {
      const { attributes } = action;
      const { is_important = false, is_hidden_from_clients = false } = attributes;
      const due_on = dateStringToUnixTimestamp(attributes.due_on);
      const start_on = dateStringToUnixTimestamp(attributes.start_on);
      return new Task({
        ...fromJS(attributes).toObject(),
        id: action.id,
        project_id: action.projectId,
        position: action.position,
        is_important: !!is_important,
        is_hidden_from_clients: !!is_hidden_from_clients,
        start_on,
        due_on,
      });
    }
    case actionTypes.CONVERT_SUBTASK_TO_TASK_SUCCESS:
    case actionTypes.ADD_TASK_SUCCESS:
    case actionTypes.CREATE_TASK_FROM_COMMENT_SUCCESS:
    case actionTypes.ADD_TASK_FROM_CONVERSATION_SUCCESS:
    case actionTypes.ADD_TASK_FROM_RECURRING_TASK_SUCCESS:
    case actionTypes.CONVERT_DISCUSSION_TO_TASK_SUCCESS:
    case actionTypes.DUPLICATE_TASK_SUCCESS: {
      return new Task({
        ...fromJS(action.response.entities.tasks[action.response.result.single]).toObject(),
      });
    }
    case actionTypes.ADD_COMMENT_FROM_EVENT_STREAM_SUCCESS:
    case actionTypes.UPDATE_COMMENT_FROM_EVENT_STREAM_SUCCESS: {
      return new Task({
        ...fromJS(action.response.entities.tasks[`${action.parentId}`]).toObject(),
      });
    }
    case actionTypes.ADD_TASK_DEPENDENCIES_LOADING: {
      return new Task({
        ...state.toObject(),
        project_id: action.projectId,
      });
    }
    case actionTypes.ADD_TASK_DEPENDENCIES_SUCCESS: {
      const { task } = action;

      if (state && task) {
        const { labels = [] } = task;
        return new Task({
          ...state.toObject(),
          ...task,
          labels: List(labels),
        });
      }

      return state;
    }
    case actionTypes.FETCH_TASK_DEPENDENCIES_SUCCESS:
    case actionTypes.FETCH_TASK_DEPENDENCIES_SUGGESTIONS_SUCCESS: {
      const { updated_task_attributes = {} } = action;
      const { labels, open_dependencies } = updated_task_attributes;
      let task = {
        ...state.toObject(),
        ...updated_task_attributes,
      };
      if (labels) {
        task.labels = List(labels);
      }
      if (open_dependencies) {
        task.open_dependencies = Map(open_dependencies);
      }

      return new Task(task);
    }
    case actionTypes.ADD_TASK_FROM_EVENT_STREAM:
    case actionTypes.COMPLETE_TASK_FROM_EVENT_STREAM:
    case actionTypes.REOPENED_TASK_FROM_EVENT_STREAM:
    case actionTypes.TASK_LIST_CHANGED_FROM_EVENT_STREAM:
    case actionTypes.UPDATE_TASK_FROM_EVENT_STREAM:
    case actionTypes.TASK_MOVED_TO_PROJECT_FROM_EVENT_STREAM: {
      const { response = {} } = action;
      const { entities = {}, result: taskId } = response;
      const { tasks = {} } = entities;

      const data = tasks[`${taskId}`];

      if (data) {
        return new Task(
          fromJS({
            ...state.toObject(),
            ...data,
          })
        );
      }

      return state;
    }
    case actionTypes.BATCH_EDIT_TASKS_SUCCESS: {
      const { response = {} } = action;
      const { entities = {} } = response;
      const { tasks = {} } = entities;

      const data = tasks[`${state.get(`id`)}`];

      if (data) {
        return new Task(
          fromJS({
            ...state.toObject(),
            ...data,
            _pending: false,
          })
        );
      }

      return state;
    }
    case actionTypes.BATCH_ADD_TASKS_SUCCESS: {
      const { response = {} } = action;
      const { entities = {} } = response;
      const { tasks = {} } = entities;

      const data = tasks[`${state.id}`];

      if (data) {
        return new Task(
          fromJS({
            ...state,
            ...data,
          })
        );
      }

      return state;
    }
    case actionTypes.TASK_REORDERED_FROM_EVENT_STREAM: {
      const { response = {}, position } = action;
      const { task_list_id } = response;

      if (task_list_id) {
        return new Task({
          ...state.toObject(),
          task_list_id,
          position,
        });
      }

      return state;
    }
    case actionTypes.ANGULAR_INIT_CACHED_TASK_FROM_ANGULAR: {
      const { response = {} } = action;
      const { entities = {}, result: taskId } = response;
      const { tasks = {} } = entities;

      return new Task(fromJS(tasks[taskId]));
    }
    case actionTypes.WORKLOAD_EDIT_TASK_LOADING: {
      return new Task({
        ...state.toObject(),
        _pending: true,
      });
    }
    case actionTypes.CHANGE_TASK: {
      const { attributes = {} } = action;
      return new Task({
        ...state.toObject(),
        ...attributes,
      });
    }
    case actionTypes.TASK_LIST_MOVE_TO_TRASH_FROM_EVENT_STREAM: {
      const { response = {} } = action;
      const { entities = {}, result: id } = response;

      if (state.get("task_list_id") !== id) {
        return state;
      }

      if (state.get("is_trashed")) {
        return new Task({
          ...state.toObject(),
          original_is_trashed: true,
        });
      }

      const { task_lists = {} } = entities;
      const { trashed_on = null, trashed_by_id = 0 } = task_lists[id];

      return new Task({
        ...state.toObject(),
        is_trashed: true,
        trashed_on,
        trashed_by_id,
      });
    }
    default:
      return state;
  }
};

const mergeEntities = (state, newTasks) => {
  newTasks.forEach((newTask) => {
    let prevState = state.get(`${newTask.get(`id`)}`);

    prevState = prevState ? prevState.toObject() : {};

    state = state.set(
      `${newTask.get(`id`)}`,
      new Task({
        ...prevState,
        ...newTask.toObject(),
        _pending: false,
      })
    );
  });

  return state;
};

const advancedMergeEntities = (state, taskIds, taskEntities, action) => {
  taskIds.forEach((id) => {
    if (taskEntities[id]) {
      state = state.set(
        `${id}`,
        task(state.get(`${id}`), {
          ...action,
          updated_task_attributes: taskEntities[id],
        })
      );
    }
  });

  return state;
};

const tasks = (state = new TaskMap(), action) => {
  switch (action.type) {
    case actionTypes.FETCH_CALENDAR_ASSIGMENTS_SUCCESS:
    case actionTypes.PROJECT_TEMPLATE_APPLY_SUCCESS: {
      const { response = {} } = action;
      const { entities = {} } = response;
      const { tasks } = entities;

      if (!tasks) {
        return state;
      }
      return mergeEntities(state, fromJS(action.response.entities.tasks));
    }
    case actionTypes.ADD_TASK_DEPENDENCIES_LOADING: {
      const { projectId = 0, dependency_id = 0 } = action;

      if (projectId > 0 && dependency_id > 0) {
        state = state.set(`${dependency_id}`, task(state.get(`${dependency_id}`), action));
      }

      return state;
    }
    case actionTypes.FETCH_TASK_DEPENDENCIES_SUCCESS:
    case actionTypes.FETCH_TASK_DEPENDENCIES_SUGGESTIONS_SUCCESS: {
      const { response = {} } = action;
      const { result = {}, entities = {} } = response;
      const { tasks: taskEntities = {} } = entities;
      const { tasks: taskIds = [], children = [], parents = [] } = result;

      if (taskIds && taskIds.length) {
        state = advancedMergeEntities(state, taskIds, taskEntities, action);
      }

      if (children && children.length) {
        state = advancedMergeEntities(state, children, taskEntities, action);
      }

      if (parents && parents.length) {
        state = advancedMergeEntities(state, parents, taskEntities, action);
      }

      return state;
    }
    case actionTypes.COMPLETE_TASK_LOADING: {
      const { taskId } = action;
      if (!taskId || !state.get(`${taskId}`)) {
        return state;
      }
      return state.set(`${taskId}`, task(state.get(`${taskId}`), action));
    }
    case actionTypes.RESCHEDULE_TASK_SUCCESS:
      if (action.error && action.error.status_code === 409) {
        return state;
      }
    // eslint-disable-next-line no-fallthrough
    case actionTypes.ADD_TASK_DEPENDENCIES_SUCCESS:
    case actionTypes.FETCH_ACTIVITY_LOGS_SUCCESS:
    case actionTypes.FETCH_TASKS_SUCCESS:
    case actionTypes.FETCH_WORKLOAD_TASKS_SUCCESS:
    case actionTypes.EDIT_TASK_SUCCESS:
    case actionTypes.OPEN_TASK_SUCCESS:
    case actionTypes.COMPLETE_TASK_SUCCESS:
    case actionTypes.COMPLETE_TASK_TOGGLER:
    case actionTypes.ANGULAR_INITIALIZE_TASKS:
    case actionTypes.FETCH_TIME_RECORDS_SUCCESS:
    case actionTypes.FETCH_FILES_AND_ATTACHMENTS_SUCCESS:
    case actionTypes.FETCH_EXPENSES_SUCCESS:
    case actionTypes.FETCH_PROJECT_LIST_TIME_RECORDS_SUCCESS:
    case actionTypes.FETCH_PROJECT_EXPENSES_SUCCESS:
    case actionTypes.FETCH_PROJECT_TIME_RECORDS_BY_DATES_SUCCESS:
    case actionTypes.FETCH_TIMESHEET_TIME_RECORDS_SUCCESS:
    case actionTypes.DUPLICATE_TASK_SUCCESS:
      if (!action.response || !action.response.entities) {
        return state;
      }
      if (action.response.result && action.response.result.trashed_task_ids) {
        const trashedTaskIds = action.response.result.trashed_task_ids;
        trashedTaskIds.forEach((taskId) => {
          if (state.get(`${taskId}`)) {
            state = state.set(`${taskId}`, task(state.get(`${taskId}`), action));
          }
        });
      }
      if (action.response.result && action.response.result.completed_task_ids) {
        const completedTaskIds = action.response.result.completed_task_ids;
        completedTaskIds.forEach((taskId) => {
          if (state.get(`${taskId}`)) {
            state = state.set(`${taskId}`, task(state.get(`${taskId}`), action));
          }
        });
      }
      if (!action.response.entities.tasks) {
        return state;
      }
      return mergeEntities(state, fromJS(action.response.entities.tasks));
    case actionTypes.EDIT_TASK_LOADING: {
      if (!action.taskId || !state.has(`${action.taskId}`)) {
        return state;
      }

      let modifiedAction = { ...action };

      if (action.attributes && action.attributes.task_list_id !== state.get(`${action.taskId}`).task_list_id) {
        let task = state
          .filter((t) => t.task_list_id === action.attributes.task_list_id)
          .sortBy((t) => t.position)
          .last();

        if (task && Number.isInteger(task.position)) {
          modifiedAction.position = task.position + 1;
        }
      }
      return state.set(`${action.taskId}`, task(state.get(`${action.taskId}`), modifiedAction));
    }
    case actionTypes.EDIT_TASK_ERROR: {
      if (action.taskId) {
        if (action._previous) {
          return state.set(`${action.taskId}`, action._previous);
        }
        return state.set(`${action.taskId}`, task(state.get(`${action.taskId}`), action));
      }
    }
    // eslint-disable-next-line no-fallthrough
    case actionTypes.OPEN_TASK_LOADING:
    case actionTypes.OPEN_TASK_ERROR:
    case actionTypes.COMPLETE_TASK_ERROR:
      if (!action.taskId || !state.has(action.taskId.toString())) {
        return state;
      }
      return state.set(`${action.taskId}`, task(state.get(`${action.taskId}`), action));
    case actionTypes.ADD_COMMENT_LOADING:
    case actionTypes.MOVE_COMMENT_TO_TRASH_SUCCESS:
    case actionTypes.FETCH_COMMENTS_LOADING:
    case actionTypes.FETCH_COMMENTS_SUCCESS:
    case actionTypes.FETCH_COMMENTS_ERROR:
      if (
        !action.parentType ||
        action.parentType !== `Task` ||
        !action.parentId ||
        !state.has(action.parentId.toString())
      ) {
        return state;
      }
      return state
        .remove(action.parentId.toString())
        .set(action.parentId.toString(), task(state.get(action.parentId.toString()), action));
    case actionTypes.MOVE_TASK_TO_TRASH_LOADING:
    case actionTypes.MOVE_TASK_TO_TRASH_ERROR:
      if (!action.objectType || action.objectType !== `Task` || !action.id || !state.has(`${action.id}`)) {
        return state;
      }
      return state.remove(`${action.id}`).set(`${action.id}`, task(state.get(`${action.id}`), action));
    case actionTypes.BATCH_EDIT_TASKS_LOADING:
    case actionTypes.BATCH_EDIT_TASKS_ERROR:
      return state.map((t) => {
        if (Array.isArray(action.taskIds) && action.taskIds.indexOf(t.id) > -1) {
          return task(t, action);
        }
        return t;
      });
    case actionTypes.REOPEN_TASK_LIST_LOADING:
    case actionTypes.REOPEN_TASK_LIST_SUCCESS:
    case actionTypes.COMPLETE_TASK_LIST_LOADING:
    case actionTypes.COMPLETE_TASK_LIST_SUCCESS: {
      const { taskListId } = action;

      return state.map((t) => {
        if (t.task_list_id === taskListId) {
          return task(t, action);
        }
        return t;
      });
    }
    case actionTypes.MOVE_TASK_LIST_TO_TRASH_SUCCESS:
      return state.map((t) => {
        if (t.task_list_id === action.id) {
          return task(t, action);
        }
        return t;
      });
    case actionTypes.REORDER_TASKS_SUCCESS: {
      if (action.response && action.response.length) {
        action.response.forEach((taskId, index) => {
          let task = state.get(`${taskId}`);

          if (task) {
            state = state.set(
              `${taskId}`,
              new Task({
                ...task.toObject(),
                position: index + 1,
                task_list_id: action.taskListId,
                _pending: false,
              })
            );
          }
        });
      }
      return state;
    }
    case actionTypes.REORDER_TASKS_ERROR:
      return state.set(`${action.sourceTaskId}`, task(state.get(`${action.sourceTaskId}`), action));
    case actionTypes.ADD_TASK_LOADING: {
      const taskWithMaxPosition = state.maxBy((task) => task.position);
      const position = taskWithMaxPosition && taskWithMaxPosition.position ? taskWithMaxPosition.position + 1 : 1;

      return state.set(`${action.id}`, task(state.get(`${action.id}`), { ...action, position }));
    }
    case actionTypes.ADD_TASK_SUCCESS:
    case actionTypes.ADD_TASK_FROM_RECURRING_TASK_SUCCESS:
    case actionTypes.CONVERT_DISCUSSION_TO_TASK_SUCCESS:
    case actionTypes.ADD_TASK_FROM_CONVERSATION_SUCCESS:
    case actionTypes.CREATE_TASK_FROM_COMMENT_SUCCESS:
      state = state.remove(action.id);
      return state.set(`${action.response.result.single}`, task(undefined, action));
    case actionTypes.ADD_COMMENT_FROM_EVENT_STREAM_SUCCESS:
    case actionTypes.UPDATE_COMMENT_FROM_EVENT_STREAM_SUCCESS:
      if (!state.get(`${action.parentId}`)) {
        return state;
      }
      state = state.remove(`${action.parentId}`);
      return state.set(`${action.parentId}`, task(undefined, action));
    case actionTypes.ADD_TASK_ERROR:
      return state.remove(action.id);
    case actionTypes.LOAD_TASK:
    case actionTypes.ANGULAR_INIT_TASK_AND_COMMENTS_MODAL: {
      const { response = {}, taskId } = action;
      const { entities = {} } = response;
      const { tasks = {} } = entities;

      const mapOfTasks = fromJS(tasks);
      let task = mapOfTasks.get(`${taskId}`);

      if (task) {
        return mergeEntities(state, mapOfTasks.set(`${taskId}`, task));
      }

      return state;
    }
    case actionTypes.CONVERT_SUBTASK_TO_TASK_SUCCESS: {
      const { response = {} } = action;
      const { result = {} } = response;
      const { single } = result;

      return single ? state.set(`${single}`, task(undefined, action)) : state;
    }
    case actionTypes.WORKLOAD_EDIT_TASK_LOADING: {
      const { taskId } = action;
      if (state.get(`${taskId}`)) {
        return state.set(`${taskId}`, task(state.get(`${taskId}`), action));
      }
      return state;
    }
    case actionTypes.ADD_TASK_FROM_EVENT_STREAM:
    case actionTypes.COMPLETE_TASK_FROM_EVENT_STREAM:
    case actionTypes.REOPENED_TASK_FROM_EVENT_STREAM:
    case actionTypes.TASK_MOVE_TO_TRASH_FROM_EVENT_STREAM:
    case actionTypes.TASK_RESTORED_FROM_TRASH_FROM_EVENT_STREAM:
    case actionTypes.UPDATE_TASK_FROM_EVENT_STREAM:
    case actionTypes.TASK_MOVED_TO_PROJECT_FROM_EVENT_STREAM: {
      const { response = {} } = action;
      const { result: taskId, entities = {} } = response;
      try {
        const prev = state.get(`${taskId}`);
        const next = entities.tasks ? entities.tasks[taskId] : undefined;
        if (prev && next && next.updated_on && next.updated_on <= prev.get("updated_on", 0)) {
          return state;
        } else {
          return state.set(`${taskId}`, task(prev, action));
        }
      } catch (e) {
        return state;
      }
    }
    case actionTypes.PROJECT_COMPLETED_FROM_EVENT_STREAM_SUCCESS: {
      const { response = {} } = action;
      const { entities = {}, result: projectId = 0 } = response;
      const { projects = {} } = entities;
      const project = projects[projectId];
      if (project) {
        return state.reduce((prev, next) => {
          if (next.project_id === projectId) {
            const updated = next
              .set("is_completed", true)
              .set("completed_on", next.completed_on ? next.completed_on : project.completed_on)
              .set("completed_by_id", next.completed_by_id ? next.completed_by_id : project.completed_by_id);
            return prev.set(`${next.id}`, updated);
          }
          return prev;
        }, state);
      }

      return state;
    }
    case actionTypes.TASK_LIST_CHANGED_FROM_EVENT_STREAM: {
      const { response = {} } = action;
      const { entities = {}, result: taskId } = response;
      const { tasks: collection = {} } = entities;

      const entity = collection[taskId];

      if (entity) {
        const { task_list_id, position } = entity;

        const task_list_tasks = state.filter((task) => {
          return task.task_list_id === task_list_id && task.position >= position;
        });

        task_list_tasks.forEach((task, key) => {
          state = state.set(
            `${key}`,
            new Task({
              ...task.toObject(),
              position: task.position + 1,
            })
          );
        });
      }

      if (taskId) {
        return state.set(`${taskId}`, task(state.get(`${taskId}`), action));
      }

      return state;
    }
    case actionTypes.TASK_LIST_COMPLETED_FROM_EVENT_STREAM: {
      const { response = {} } = action;
      const { entities = {}, result: taskListId = 0 } = response;
      const { task_lists = {} } = entities;
      const taskList = task_lists[taskListId];
      if (taskList) {
        return state.reduce((prev, next) => {
          if (next.task_list_id === taskListId) {
            const updated = next
              .set("is_completed", true)
              .set("completed_on", next.completed_on ? next.completed_on : taskList.completed_on)
              .set("completed_by_id", next.completed_by_id ? next.completed_by_id : taskList.completed_by_id);
            return prev.set(`${next.id}`, updated);
          }
          return prev;
        }, state);
      }

      return state;
    }
    case actionTypes.TASK_REORDERED_FROM_EVENT_STREAM: {
      const { response = {} } = action;
      const { ordered_task_ids = [] } = response;

      ordered_task_ids.forEach((id, key) => {
        state = state.set(
          `${id}`,
          task(state.get(`${id}`), {
            ...action,
            position: key + 1,
          })
        );
      });

      return state;
    }
    case actionTypes.ANGULAR_INIT_CACHED_TASK_FROM_ANGULAR: {
      const { response = {} } = action;
      const { result: taskId } = response;

      if (taskId && !state.get(`${taskId}`)) {
        return state.set(`${taskId}`, task(state.get(`${taskId}`), action));
      }

      return state;
    }
    case actionTypes.FLUSH_ALL_TASKS_FROM_STORE:
      return new TaskMap();
    case actionTypes.RESTORE_TASK_FROM_TRASH_SUCCESS:
    case actionTypes.MOVE_TASK_TO_TRASH_SUCCESS: {
      const { id } = action;
      return state.set(`${id}`, task(state.get(`${id}`), action));
    }
    case actionTypes.CHANGE_TASK: {
      const { id, tasksOrder = [] } = action;

      state = state.set(`${id}`, task(state.get(`${id}`), action));

      tasksOrder.forEach((taskId, index) => {
        state = state.set(
          `${taskId}`,
          new Task({
            ...state.get(`${taskId}`).toObject(),
            position: index + 1,
          })
        );
      });

      return state;
    }
    case actionTypes.BATCH_EDIT_TASKS_SUCCESS: {
      const { response = {} } = action;
      const { result = [] } = response;
      result.forEach((id) => (state = state.set(`${id}`, task(state.get(`${id}`), action))));
      return state;
    }
    case actionTypes.BATCH_ADD_TASKS_SUCCESS: {
      const { response = {} } = action;
      const { entities = {}, result = [] } = response;
      const { tasks = {} } = entities;
      result.forEach((id) => (state = state.set(`${id}`, task(tasks[id], action))));
      return state;
    }
    case actionTypes.STOPWATCHES_FETCH_SUCCESS: {
      const { response = {} } = action;
      const { entities = {} } = response;
      const { tasks = [] } = entities;

      return mergeEntities(state, fromJS(tasks));
    }
    case actionTypes.FETCH_USER_TASKS_SUCCESS: {
      const { response = {} } = action;
      const { entities = {} } = response;
      const { tasks } = entities;

      if (tasks) {
        return mergeEntities(state, fromJS(tasks));
      }

      return state;
    }
    case actionTypes.PROJECT_MOVE_TO_TRASH_FROM_EVENT_STREAM_SUCCESS: {
      const { response = {} } = action;
      const { entities = {}, result: id } = response;
      const { projects = {} } = entities;
      const { trashed_on = null, trashed_by_id = 0 } = projects[id];

      return state.map((task) => {
        if (task.get("project_id") === id) {
          return task
            .set("original_is_trashed", task.is_trashed)
            .set("is_trashed", true)
            .set("trashed_on", task.trashed_on ? task.trashed_on : trashed_on)
            .set("trashed_by_id", task.trashed_by_id ? task.trashed_by_id : trashed_by_id);
        }
        return task;
      });
    }
    case actionTypes.TASK_LIST_MOVE_TO_TRASH_FROM_EVENT_STREAM: {
      const keys = state.keySeq();

      keys.forEach((key) => {
        state = state.set(key, task(state.get(key), action));
      });

      return state;
    }
    case actionTypes.DATA_OBJECT_DELETED_SUCCESS: {
      if (action.objectType === "Task") {
        return state.filter((task) => task.id !== action.id);
      }
      return state;
    }
    case actionTypes.MOVE_TASK_TO_PROJECT_SUCCESS: {
      const { projectId = 0, newProjectId = 0, response = {} } = action;
      const { entities = {}, result = {} } = response;
      const { tasks = {} } = entities;
      const { single: taskId = 0 } = result;

      const task = tasks[taskId] ?? {};

      if (task && projectId !== newProjectId) {
        state = state.set(
          `${taskId}`,
          new Task({
            ...fromJS(task).toObject(),
          })
        );
      }
      return state;
    }
    case actionTypes.DELETE_TASK_DEPENDENCIES_SUCCESS: {
      const { taskId, is_parent, dependency_id } = action;

      const task = state.get(`${taskId}`);
      const dependency = state.get(`${dependency_id}`);

      let updatedTask, updatedDependency;

      if (is_parent) {
        updatedTask = task?.updateIn(["open_dependencies", "parents_count"], (value) => value - 1);
        updatedDependency = dependency?.updateIn(["open_dependencies", "children_count"], (value) => value - 1);
      } else {
        updatedTask = task?.updateIn(["open_dependencies", "children_count"], (value) => value - 1);
        updatedDependency = dependency?.updateIn(["open_dependencies", "parents_count"], (value) => value - 1);
      }

      if (updatedTask) {
        state = state.set(`${taskId}`, updatedTask);
      }

      if (updatedDependency) {
        state = state.set(`${dependency_id}`, updatedDependency);
      }

      return state;
    }
    case actionTypes.MOVE_USER_TO_ARCHIVE_FROM_EVENT_STREAM_SUCCESS:
    case actionTypes.USER_MOVE_TO_TRASH_FROM_EVENT_STREAM_SUCCESS: {
      const { response = {} } = action;
      const { result: id } = response;
      return state.map((task) => {
        if (task.get("assignee_id") === id) {
          return task.set("assignee_id", 0);
        }
        return task;
      });
    }
    case actionTypes.FETCH_TASKS_ARCHIVE_SUCCESS: {
      if (!action.response) return state;
      const filteredTasks = action.response.filter((item) => {
        if (item.class === "Task") return true;
      });
      return mergeEntities(state, fromJS(filteredTasks));
    }
    case actionTypes.FLUSH_TASKS_ARCHIVE: {
      const projectId = action.payload.projectId;
      if (!projectId) {
        return state;
      }
      state.forEach((task) => {
        if (task.is_completed && !task.is_trashed && task.project_id === projectId) {
          state = state.delete(`${task.id}`);
        }
      });
      return state;
    }
    case actionTypes.FETCH_TEAM_TASKS_SUCCESS: {
      const { response } = action;
      const { entities = {} } = response;
      const { tasks = {} } = entities;

      return mergeEntities(state, fromJS(tasks));
    }
    default: {
      return state;
    }
  }
};

export default tasks;
